import React, { FC } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MuiButton from "@mui/material/Button";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import Select from "@/components/Select";
import { GoalTemplateFormData } from "./validation.schema";

type Props = {
  stepIndex: number;
  onRemove: () => void;
  canRemove: boolean;
  languageOptions: { key: string; value: string }[];
  rewardConfigOptions: { key: string; value: string }[];
  isLoadingRewardConfigs: boolean;
};

const EMPTY_LANGUAGE = { language: "", title: "", description: "" };

const StepFields: FC<Props> = ({
  stepIndex,
  onRemove,
  canRemove,
  languageOptions,
  rewardConfigOptions,
  isLoadingRewardConfigs,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<GoalTemplateFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `steps.${stepIndex}.languages` as const,
  });

  const stepErrors = errors.steps?.[stepIndex];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="bodyMedium" sx={{ fontWeight: 600 }}>
          Step #{stepIndex + 1}
        </Typography>
        <IconButton
          size="small"
          disabled={!canRemove}
          onClick={onRemove}
          aria-label="remove step"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={"16px"}>
        <Grid item xs={12} sm={5}>
          <Controller
            name={`steps.${stepIndex}.name` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`step-name-${stepIndex}`}
                label="Name"
                variant="outlined"
                required
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={!!stepErrors?.name}
                helperText={stepErrors?.name?.message || null}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name={`steps.${stepIndex}.status` as const}
            control={control}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={[
                  { key: "ACTIVE", value: "Active" },
                  { key: "INACTIVE", value: "Inactive" },
                ]}
                value={field.value as string}
                label="Status"
                id={`step-status-${stepIndex}`}
                required
                error={!!stepErrors?.status}
                errorMessage={stepErrors?.status?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name={`steps.${stepIndex}.order` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`step-order-${stepIndex}`}
                label="Order"
                type="number"
                variant="outlined"
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === "" ? undefined : Number(e.target.value)
                  )
                }
                error={!!stepErrors?.order}
                helperText={stepErrors?.order?.message || null}
                inputProps={{ min: 0 }}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`steps.${stepIndex}.rewardConfigurationId` as const}
            control={control}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={rewardConfigOptions}
                value={field.value as string}
                label="Reward config (override)"
                id={`step-reward-config-${stepIndex}`}
                isLoading={isLoadingRewardConfigs}
                showClearOption
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="bodyMedium" sx={{ fontWeight: 500 }}>
              Languages
            </Typography>
            <MuiButton
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => append(EMPTY_LANGUAGE)}
            >
              Add language
            </MuiButton>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {fields.map((langField, langIndex) => {
              const langErrors = stepErrors?.languages?.[langIndex];
              return (
                <Grid container spacing={"12px"} key={langField.id}>
                  <Grid item xs={12} sm={3}>
                    <Controller
                      name={
                        `steps.${stepIndex}.languages.${langIndex}.language` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <Select
                          handleChange={(value) => field.onChange(value)}
                          data={languageOptions}
                          value={field.value as string}
                          label="Language"
                          id={`step-lang-${stepIndex}-${langIndex}`}
                          required
                          error={!!langErrors?.language}
                          errorMessage={langErrors?.language?.message || ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={
                        `steps.${stepIndex}.languages.${langIndex}.title` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`step-title-${stepIndex}-${langIndex}`}
                          label="Title"
                          variant="outlined"
                          required
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          error={!!langErrors?.title}
                          helperText={langErrors?.title?.message || null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={11} sm={4}>
                    <Controller
                      name={
                        `steps.${stepIndex}.languages.${langIndex}.description` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`step-desc-${stepIndex}-${langIndex}`}
                          label="Description"
                          variant="outlined"
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sm={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => remove(langIndex)}
                      aria-label="remove language"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default StepFields;
